import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Building2, MapPin, User, Wrench, FileText, TrendingDown, Plus,
  Trash2, ClipboardCheck, Package,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { MapaGPS } from '@/components/ui/MapaGPS';
import { cn } from '@/lib/cn';

type Bien = any;
type Tab = 'info' | 'inmueble' | 'resguardos' | 'mantenimientos' | 'depreciacion';

export function BienDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('info');
  const [resguardoOpen, setResguardoOpen] = useState(false);
  const [mantOpen, setMantOpen] = useState(false);
  const [bajaOpen, setBajaOpen] = useState(false);

  const { data: b, refetch } = useQuery<Bien>({
    queryKey: ['bien', id],
    queryFn: () => api(`/bienes/${id}`),
  });

  if (!b) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  const esInmueble = b.tipo === 'inmueble';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/bienes" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a bienes
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={cn('flex h-12 w-12 items-center justify-center rounded-lg',
              esInmueble ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-600')}>
              {esInmueble ? <Building2 size={20} /> : <Package size={20} />}
            </div>
            <div>
              <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700 dark:text-brand-300">{b.clave}</p>
              <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">{b.descripcion}</h1>
              <p className="text-[12px] text-ink-mute capitalize">{b.tipo} · {b.categoria_nombre ?? 'sin categoría'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {b.estado === 'activo' && (
              <Button variant="ghost" onClick={() => setBajaOpen(true)} className="text-danger hover:bg-rose-50">
                <Trash2 size={14} /> Dar de baja
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat label="Valor en libros" value={fmt.money(b.valor_en_libros)} accent="brand" />
        <Stat label="Valor adquisición" value={fmt.money(b.valor_adquisicion)} />
        <Stat label="Depreciación acum." value={fmt.money(b.depreciacion_acumulada)} />
        <Stat label="Estado" value={b.estado} capitalize />
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        <TabBtn active={tab === 'info'} onClick={() => setTab('info')}>Información</TabBtn>
        {esInmueble && <TabBtn active={tab === 'inmueble'} onClick={() => setTab('inmueble')}>Catastro y mapa</TabBtn>}
        <TabBtn active={tab === 'resguardos'} onClick={() => setTab('resguardos')}>Resguardos</TabBtn>
        <TabBtn active={tab === 'mantenimientos'} onClick={() => setTab('mantenimientos')}>Mantenimientos</TabBtn>
        <TabBtn active={tab === 'depreciacion'} onClick={() => setTab('depreciacion')}>Depreciación</TabBtn>
      </div>

      {tab === 'info' && <InfoTab b={b} />}
      {tab === 'inmueble' && esInmueble && <InmuebleTab bienID={id} />}
      {tab === 'resguardos' && <ResguardosTab bienID={id} onNuevo={() => setResguardoOpen(true)} />}
      {tab === 'mantenimientos' && <MantenimientosTab bienID={id} onNuevo={() => setMantOpen(true)} />}
      {tab === 'depreciacion' && <DepreciacionBienTab bienID={id} />}

      <ResguardoModal bienID={id} open={resguardoOpen}
        onClose={() => setResguardoOpen(false)}
        onDone={() => { setResguardoOpen(false); qc.invalidateQueries({ queryKey: ['resguardos', id] }); refetch(); }} />
      <MantenimientoModal bienID={id} open={mantOpen}
        onClose={() => setMantOpen(false)}
        onDone={() => { setMantOpen(false); qc.invalidateQueries({ queryKey: ['mantenimientos', id] }); refetch(); }} />
      <BajaModal bienID={id} open={bajaOpen}
        onClose={() => setBajaOpen(false)}
        onDone={() => { setBajaOpen(false); navigate('/app/bienes'); }} />
    </div>
  );
}

function TabBtn({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={cn('relative px-4 py-2.5 text-[13px] font-medium transition-colors',
        active ? 'text-brand-600' : 'text-ink-mute hover:text-ink')}>
      {children}
      {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-brand-500" />}
    </button>
  );
}

function Stat({ label, value, accent = 'neutral', capitalize }: { label: string; value: string; accent?: 'brand' | 'neutral'; capitalize?: boolean }) {
  const bar = accent === 'brand' ? 'before:bg-brand-500' : 'before:bg-ink-soft';
  return (
    <div className={cn(
      'relative overflow-hidden rounded-lg border border-border bg-surface p-5 shadow-card',
      'before:absolute before:inset-x-0 before:top-0 before:h-[3px]', bar,
    )}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className={cn('num mt-3 text-[20px] font-semibold text-ink', capitalize && 'capitalize')}>{value}</div>
    </div>
  );
}

function InfoTab({ b }: { b: Bien }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Card>
        <CardHeader title="Identificación" />
        <CardBody className="flex flex-col gap-2 text-[13px]">
          <KV label="Clave" value={<span className="num font-semibold">{b.clave}</span>} />
          <KV label="Descripción" value={b.descripcion} />
          <KV label="Tipo" value={<span className="capitalize">{b.tipo}</span>} />
          <KV label="Categoría" value={b.categoria_nombre ?? '—'} />
          <KV label="Marca" value={b.marca ?? '—'} />
          <KV label="Modelo" value={b.modelo ?? '—'} />
          <KV label="Núm. serie" value={b.numero_serie ?? '—'} />
          <KV label="Color" value={b.color ?? '—'} />
          <KV label="Condiciones" value={b.condiciones ?? '—'} />
          <KV label="Características" value={b.caracteristicas ?? '—'} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Valor y depreciación" />
        <CardBody className="flex flex-col gap-2 text-[13px]">
          <KV label="Fecha adquisición" value={fmt.date(b.fecha_adquisicion)} />
          <KV label="Valor adquisición" value={<span className="num font-semibold">{fmt.money(b.valor_adquisicion)}</span>} />
          <KV label="Valor residual" value={<span className="num">{fmt.money(b.valor_residual)}</span>} />
          <KV label="Depre. acumulada" value={<span className="num">{fmt.money(b.depreciacion_acumulada)}</span>} />
          <KV label="Valor en libros" value={<span className="num font-semibold text-emerald-700">{fmt.money(b.valor_en_libros)}</span>} />
          <KV label="Vida útil" value={b.vida_util_meses ? `${b.vida_util_meses} meses` : '—'} />
          <KV label="Garantía hasta" value={b.garantia_hasta ? fmt.date(b.garantia_hasta) : '—'} />
          <KV label="Último mantenimiento" value={b.ultimo_mantenimiento ? fmt.date(b.ultimo_mantenimiento) : '—'} />
          <KV label="Próximo mantenimiento" value={b.proximo_mantenimiento ? fmt.date(b.proximo_mantenimiento) : '—'} />
          {b.kilometraje != null && <KV label="Kilometraje" value={`${fmt.num(b.kilometraje)} km`} />}
          <KV label="Proveedor" value={b.proveedor_nombre ?? '—'} />
          <KV label="Unidad administrativa" value={b.unidad_nombre ?? '—'} />
          <KV label="Ubicación" value={b.ubicacion ?? '—'} />
          <KV label="Resguardante actual" value={b.responsable_nombre ?? 'sin asignar'} />
        </CardBody>
      </Card>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-border/60 pb-1.5 last:border-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}

// --- Tab catastro / mapa ---

function InmuebleTab({ bienID }: { bienID: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<any>({
    queryKey: ['inmueble', bienID],
    queryFn: () => api(`/bienes/${bienID}/inmueble`),
  });
  const [v, setV] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Hidratar form al cargar
  if (data && v === null) {
    setV({ ...data });
  }
  if (isLoading || v === null) return <div className="p-10 text-center text-[13px] text-ink-mute">Cargando…</div>;

  async function guardar() {
    setSaving(true); setErr(null);
    try {
      await api(`/bienes/${bienID}/inmueble`, { method: 'PATCH', body: v });
      qc.invalidateQueries({ queryKey: ['inmueble', bienID] });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.2fr]">
      <Card>
        <CardHeader title="Datos catastrales" subtitle="Dirección oficial, folio registral, cuenta catastral, superficies y valores" />
        <CardBody className="flex flex-col gap-3">
          {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[13px] font-medium text-ink">Tipo de inmueble</label>
              <select value={v.tipo_inmueble} onChange={(e) => setV({ ...v, tipo_inmueble: e.target.value })}
                className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
                <option value="terreno">Terreno</option>
                <option value="edificio">Edificio</option>
                <option value="vivienda">Vivienda</option>
                <option value="infraestructura">Infraestructura</option>
                <option value="construccion_proceso">Construcción en proceso</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <Input name="calle" label="Calle" value={v.calle ?? ''} onChange={(e) => setV({ ...v, calle: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input name="numero_exterior" label="Núm. ext." value={v.numero_exterior ?? ''} onChange={(e) => setV({ ...v, numero_exterior: e.target.value })} />
              <Input name="numero_interior" label="Núm. int." value={v.numero_interior ?? ''} onChange={(e) => setV({ ...v, numero_interior: e.target.value })} />
            </div>
            <Input name="colonia" label="Colonia" value={v.colonia ?? ''} onChange={(e) => setV({ ...v, colonia: e.target.value })} />
            <Input name="codigo_postal" label="CP" value={v.codigo_postal ?? ''} onChange={(e) => setV({ ...v, codigo_postal: e.target.value })} />
            <Input name="localidad" label="Localidad" value={v.localidad ?? ''} onChange={(e) => setV({ ...v, localidad: e.target.value })} />
            <Input name="municipio" label="Municipio" value={v.municipio ?? ''} onChange={(e) => setV({ ...v, municipio: e.target.value })} />
            <Input name="entidad_federativa" label="Entidad federativa" value={v.entidad_federativa ?? ''} onChange={(e) => setV({ ...v, entidad_federativa: e.target.value })} />
            <Input name="pais" label="País" value={v.pais ?? 'México'} onChange={(e) => setV({ ...v, pais: e.target.value })} />
            <Input name="cuenta_catastral" label="Cuenta catastral" value={v.cuenta_catastral ?? ''} onChange={(e) => setV({ ...v, cuenta_catastral: e.target.value })} />
            <Input name="folio_registral" label="Folio registral" value={v.folio_registral ?? ''} onChange={(e) => setV({ ...v, folio_registral: e.target.value })} />
            <Input name="superficie_terreno_m2" label="Superficie terreno (m²)" type="number" step="0.01"
              value={v.superficie_terreno_m2 ?? ''} onChange={(e) => setV({ ...v, superficie_terreno_m2: parseFloat(e.target.value) || null })} />
            <Input name="superficie_construccion_m2" label="Superficie construcción (m²)" type="number" step="0.01"
              value={v.superficie_construccion_m2 ?? ''} onChange={(e) => setV({ ...v, superficie_construccion_m2: parseFloat(e.target.value) || null })} />
            <Input name="valor_catastral" label="Valor catastral" type="number" step="0.01"
              value={v.valor_catastral ?? ''} onChange={(e) => setV({ ...v, valor_catastral: parseFloat(e.target.value) || null })} />
            <Input name="valor_comercial" label="Valor comercial (avalúo)" type="number" step="0.01"
              value={v.valor_comercial ?? ''} onChange={(e) => setV({ ...v, valor_comercial: parseFloat(e.target.value) || null })} />
            <Input name="fecha_avaluo" label="Fecha avalúo" type="date"
              value={v.fecha_avaluo ?? ''} onChange={(e) => setV({ ...v, fecha_avaluo: e.target.value })} />
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft mb-1">Servicios disponibles</p>
            <div className="grid grid-cols-3 gap-2 text-[12px]">
              {[
                ['tiene_agua', 'Agua'],
                ['tiene_luz', 'Luz'],
                ['tiene_drenaje', 'Drenaje'],
                ['tiene_gas', 'Gas'],
                ['tiene_internet', 'Internet'],
              ].map(([k, lbl]) => (
                <label key={k} className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={!!v[k]} onChange={(e) => setV({ ...v, [k]: e.target.checked })}
                    className="h-3.5 w-3.5 accent-brand-500" />
                  {lbl}
                </label>
              ))}
            </div>
          </div>

          <Button onClick={guardar} loading={saving}>Guardar catastro</Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Georreferenciación"
          subtitle="Click en el mapa para fijar la ubicación. OpenStreetMap."
          action={v.latitud && v.longitud ?
            <span className="num rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
              <MapPin size={11} className="inline -mt-0.5" /> {v.latitud.toFixed(5)}, {v.longitud.toFixed(5)}
            </span> : undefined}
        />
        <CardBody>
          <MapaGPS
            latitud={v.latitud ?? null}
            longitud={v.longitud ?? null}
            onChange={(lat, lon) => setV({ ...v, latitud: lat, longitud: lon })}
            height={420}
          />
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Input name="latitud" label="Latitud" type="number" step="0.0000001"
              value={v.latitud ?? ''} onChange={(e) => setV({ ...v, latitud: parseFloat(e.target.value) || null })} />
            <Input name="longitud" label="Longitud" type="number" step="0.0000001"
              value={v.longitud ?? ''} onChange={(e) => setV({ ...v, longitud: parseFloat(e.target.value) || null })} />
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// --- Tab resguardos ---

function ResguardosTab({ bienID, onNuevo }: { bienID: string; onNuevo: () => void }) {
  const { data: rows } = useQuery<any[]>({
    queryKey: ['resguardos', bienID],
    queryFn: () => api(`/bienes/${bienID}/resguardos`),
  });
  return (
    <Card>
      <CardHeader title="Historial de resguardos"
        subtitle="Un bien solo puede tener un resguardo activo a la vez; se libera automáticamente al asignar otro"
        action={<Button onClick={onNuevo}><User size={14} /> Asignar</Button>} />
      <CardBody className="p-0">
        {!rows || rows.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-ink-mute">Sin resguardos.</div>
        ) : (
          <table className="min-w-full text-[13px]">
            <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
              <tr>
                <th className="px-5 py-3">Responsable</th>
                <th className="px-5 py-3">Unidad</th>
                <th className="px-5 py-3">Ubicación</th>
                <th className="px-5 py-3">Desde</th>
                <th className="px-5 py-3">Hasta</th>
                <th className="px-5 py-3">Acta</th>
                <th className="px-5 py-3">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-5 py-3 font-medium">{r.responsable_nombre}</td>
                  <td className="px-5 py-3 text-ink-mute">{r.unidad_nombre ?? '—'}</td>
                  <td className="px-5 py-3 text-ink-mute">{r.ubicacion_fisica ?? '—'}</td>
                  <td className="num px-5 py-3">{fmt.date(r.fecha_resguardo)}</td>
                  <td className="num px-5 py-3">{r.fecha_devolucion ? fmt.date(r.fecha_devolucion) : '—'}</td>
                  <td className="px-5 py-3">
                    {r.acta_documento_id ?
                      <a href={`/api/documentos/${r.acta_documento_id}/descargar`} target="_blank"
                         className="inline-flex items-center gap-1 text-[11px] text-brand-700 hover:underline">
                        <FileText size={12} /> Descargar
                      </a> : <span className="text-ink-soft">—</span>
                    }
                  </td>
                  <td className="px-5 py-3">
                    {r.activo
                      ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Vigente</span>
                      : <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-ink-mute">Liberado</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

// --- Tab mantenimientos ---

function MantenimientosTab({ bienID, onNuevo }: { bienID: string; onNuevo: () => void }) {
  const { data: rows } = useQuery<any[]>({
    queryKey: ['mantenimientos', bienID],
    queryFn: () => api(`/bienes/${bienID}/mantenimientos`),
  });
  return (
    <Card>
      <CardHeader title="Historial de mantenimientos"
        action={<Button onClick={onNuevo}><Wrench size={14} /> Registrar</Button>} />
      <CardBody className="p-0">
        {!rows || rows.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-ink-mute">Sin mantenimientos.</div>
        ) : (
          <table className="min-w-full text-[13px]">
            <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
              <tr>
                <th className="px-5 py-3">Fecha</th>
                <th className="px-5 py-3">Tipo</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3">Proveedor</th>
                <th className="px-5 py-3 text-right">Costo</th>
                <th className="px-5 py-3 text-right">Km</th>
                <th className="px-5 py-3">Próximo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((m) => (
                <tr key={m.id}>
                  <td className="num px-5 py-3">{fmt.date(m.fecha)}</td>
                  <td className="px-5 py-3"><span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-700">{m.tipo}</span></td>
                  <td className="px-5 py-3 max-w-[280px] truncate">{m.descripcion}</td>
                  <td className="px-5 py-3 text-ink-mute">{m.proveedor_nombre ?? m.realizado_por ?? '—'}</td>
                  <td className="num px-5 py-3 text-right">{fmt.money(m.costo)}</td>
                  <td className="num px-5 py-3 text-right">{m.kilometraje ? fmt.num(m.kilometraje) : '—'}</td>
                  <td className="num px-5 py-3">{m.siguiente_fecha ? fmt.date(m.siguiente_fecha) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

// --- Tab depreciación del bien ---

function DepreciacionBienTab({ bienID: _ }: { bienID: string }) {
  return (
    <Card>
      <CardHeader title="Depreciación" subtitle="El detalle de corridas por bien se consulta desde el panel general de Depreciación" />
      <CardBody>
        <p className="text-[13px] text-ink-mute">
          Para correr la depreciación mensual de todos los bienes, entra a <strong>Recursos Materiales → Depreciación</strong>.
        </p>
      </CardBody>
    </Card>
  );
}

// ---------- Modales ----------

function ResguardoModal({ bienID, open, onClose, onDone }: { bienID: string; open: boolean; onClose: () => void; onDone: () => void }) {
  const [usuarioID, setUsuarioID] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [observ, setObserv] = useState('');
  const [subm, setSubm] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: usuarios } = useQuery({
    queryKey: ['usuarios-pick'], queryFn: async () => {
      // Si no existe endpoint, usamos /api/auth/me (al menos el admin actual)
      const me = await api<any>('/auth/me');
      return [{ id: me.usuario.id, nombre: me.usuario.nombre }];
    }, enabled: open,
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api(`/bienes/${bienID}/resguardos`, {
        method: 'POST',
        body: { responsable_usuario_id: usuarioID, ubicacion_fisica: ubicacion, observaciones: observ },
      });
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Asignar resguardo"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="resg-form" loading={subm}>Asignar</Button>
      </>}>
      <form id="resg-form" onSubmit={submit} className="flex flex-col gap-3">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div>
          <label className="text-[13px] font-medium text-ink">Responsable</label>
          <select value={usuarioID} onChange={(e) => setUsuarioID(e.target.value)} required
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
            <option value="">(seleccione)</option>
            {(usuarios ?? []).map((u: any) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
        </div>
        <Input name="ubicacion" label="Ubicación física"
          placeholder="Ej: Oficina 204, Edificio A"
          value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} />
        <div>
          <label className="text-[13px] font-medium text-ink">Observaciones</label>
          <textarea rows={3} value={observ} onChange={(e) => setObserv(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle" />
        </div>
      </form>
    </Modal>
  );
}

function MantenimientoModal({ bienID, open, onClose, onDone }: { bienID: string; open: boolean; onClose: () => void; onDone: () => void }) {
  const [tipo, setTipo] = useState('preventivo');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState('');
  const [costo, setCosto] = useState(0);
  const [km, setKm] = useState('');
  const [sig, setSig] = useState('');
  const [realiz, setRealiz] = useState('');
  const [subm, setSubm] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api(`/bienes/${bienID}/mantenimientos`, {
        method: 'POST',
        body: {
          tipo, fecha, descripcion: desc, costo,
          kilometraje: km ? parseFloat(km) : undefined,
          siguiente_fecha: sig || undefined,
          realizado_por: realiz,
        },
      });
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Registrar mantenimiento"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="mant-form" loading={subm}>Guardar</Button>
      </>}>
      <form id="mant-form" onSubmit={submit} className="flex flex-col gap-3">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[13px] font-medium text-ink">Tipo</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="preventivo">Preventivo</option>
              <option value="correctivo">Correctivo</option>
              <option value="verificacion">Verificación</option>
              <option value="afinacion">Afinación</option>
              <option value="reparacion">Reparación</option>
            </select>
          </div>
          <Input name="fecha" label="Fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </div>
        <div>
          <label className="text-[13px] font-medium text-ink">Descripción</label>
          <textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} required
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input name="costo" label="Costo" type="number" step="0.01" value={costo}
            onChange={(e) => setCosto(parseFloat(e.target.value) || 0)} />
          <Input name="km" label="Kilometraje (si aplica)" type="number" value={km} onChange={(e) => setKm(e.target.value)} />
          <Input name="realiz" label="Realizado por" value={realiz} onChange={(e) => setRealiz(e.target.value)} />
          <Input name="sig" label="Próximo mantenimiento" type="date" value={sig} onChange={(e) => setSig(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}

function BajaModal({ bienID, open, onClose, onDone }: { bienID: string; open: boolean; onClose: () => void; onDone: () => void }) {
  const [motivo, setMotivo] = useState('obsolescencia');
  const [desc, setDesc] = useState('');
  const [recup, setRecup] = useState(0);
  const [subm, setSubm] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api(`/bienes/${bienID}/baja`, {
        method: 'POST',
        body: { motivo, descripcion: desc, importe_recuperado: recup },
      });
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Dar de baja bien patrimonial"
      subtitle="Genera póliza contable que cancela el costo original y la depreciación acumulada. No permitido si tiene resguardo activo."
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="baja-form" loading={subm} variant="danger">Dar de baja</Button>
      </>}>
      <form id="baja-form" onSubmit={submit} className="flex flex-col gap-3">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div>
          <label className="text-[13px] font-medium text-ink">Motivo</label>
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
            <option value="obsolescencia">Obsolescencia</option>
            <option value="destruccion">Destrucción</option>
            <option value="robo">Robo</option>
            <option value="extravio">Extravío</option>
            <option value="venta">Venta</option>
            <option value="donacion">Donación</option>
            <option value="transferencia_externa">Transferencia externa</option>
            <option value="siniestro">Siniestro</option>
            <option value="subasta">Subasta</option>
            <option value="otro">Otro</option>
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium text-ink">Descripción / Fundamento</label>
          <textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} required
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle" />
        </div>
        <Input name="recup" label="Importe recuperado (venta / subasta)" type="number" step="0.01"
          value={recup} onChange={(e) => setRecup(parseFloat(e.target.value) || 0)} />
      </form>
    </Modal>
  );
}

// eslint sentinels
// @ts-expect-error unused
const _1 = ClipboardCheck;
// @ts-expect-error unused
const _2 = Plus;
// @ts-expect-error unused
const _3 = TrendingDown;
